import React, { useCallback, useState } from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import { useDropzone } from "react-dropzone";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import { Alert, Avatar, Button, Divider, List, ListItem, ListItemSecondaryAction, ListItemText, Paper, Stack, Typography, Zoom } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { MiniLoader } from "../Loader/Loader";

const MAX_FILES = 5;
const MAX_FILE_SIZE_MB = 5; // 5MB

const FileUploadDropzone = ({
  isLoading = false,
  isMultiple = false,
  uploadPostImage,
  fileData,
}) => {

  const files = fileData?.imageUrl
    ? [
      {
        file: new File([fileData.imageUrl], "announcement-post-image", { type: "image/*" }),
        id: fileData?._id || "",
        fileUrl: fileData?.imageUrl,
      },
    ]
    : [];

  const [message, setMessage] = useState("");

  const onDrop = useCallback(
    (acceptedFiles) => {
      const oversizedFiles = acceptedFiles.filter((file) => file.size > MAX_FILE_SIZE_MB * 1024 * 1024); // convert to MB
      if (oversizedFiles.length > 0) {
        setMessage(`Each file must be less than ${MAX_FILE_SIZE_MB} MB.`);
        return;
      }

      if (files.length > MAX_FILES) {
        setMessage("You can upload a maximum of 5 files. Please delete one to add another.");
        return;
      }

      const newFiles = acceptedFiles.slice(0, MAX_FILES - files.length).map((file) => ({
        file,
        id: uuidv4(),
      }));

      uploadPostImage(newFiles.map((file) => file.file)); // Enable it when we need to save to backend

      setMessage("");
    },
    [files],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: isMultiple,
    accept: {
      "image/*": [],
    },
  });

  const viewFile = (fileUrl) => {
    if (fileUrl) {
      window.open(fileUrl, "_blank");
    }
  };

  const isfileUploadEnabled = files?.length <= 1;
  const dropZoneBgColor = isfileUploadEnabled ? "#eeeeee" : "#f5f5f5";

  return (
    <Stack width="100%" maxWidth={700} direction="column" alignItems="stretch" alignContent="stretch" rowGap={files.length > 0 ? 3 : 0} mt={2}>
      <Paper
        {...getRootProps()}
        hasBoxShadow
        hasBorderRadius
        sx={{
          border: `3px dashed #bdbdbd`,
          ...(isfileUploadEnabled && { cursor: "pointer" }),
          bgcolor: isDragActive ? "#e3f2fd" : dropZoneBgColor,
          transition: "background-color 0.3s ease",
          "&:hover": {
            bgcolor: "#e3f2fd",
          },
        }}
      >
        <Stack direction="column" alignItems="center" justifyContent="center" rowGap={2}>
          <input {...getInputProps()} disabled={!isfileUploadEnabled} />
          <UploadFileIcon color="primary" sx={{ fontSize: 48 }} />
          <Typography level="body-lg" gutterBottom>
            {isDragActive ? "Drop the files here..." : "Drag & drop or click to upload"}
          </Typography>
          <Typography level="body-sm">You can upload image file. Max size: {MAX_FILE_SIZE_MB} MB.</Typography>
        </Stack>
      </Paper>

      {message && (
        <Zoom in>
          <Alert severity="warning" sx={{ mt: 3 }}>
            {message}
          </Alert>
        </Zoom>
      )}

      <Stack flexDirection="column" rowGap={4}>
        {/* <Typography level="body-lg">Uploaded Image</Typography> */}
        {files.length > 0 && !isLoading && (
          <Paper variant="outlined" hasBorderRadius={true} sx={{ padding: 0 }}>
            <List dense disablePadding>
              {files.map((f, idx) => (
                <React.Fragment key={f.id}>
                  <ListItem
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      py: 1.5,
                      px: 2,
                    }}
                  >
                    <Avatar sx={{ bgcolor: "#B794F4", mr: 2 }}>
                      <DescriptionIcon fontSize="small" />
                    </Avatar>

                    <ListItemText primary={<Typography level="body-md">{f.file.name}</Typography>} />
                    <ListItemSecondaryAction>
                      <Button variant="secondary" onClick={() => viewFile(f.fileUrl || "")} size="md">
                        View File
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {idx < files.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}

        {isLoading && <MiniLoader />}
      </Stack>
    </Stack>
  );
};

export default FileUploadDropzone;
